import { useState, useEffect, useRef, useMemo } from 'react'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Cards from '@cloudscape-design/components/cards'
import Link from '@cloudscape-design/components/link'
import Pagination from '@cloudscape-design/components/pagination'
import PropertyFilter from '@cloudscape-design/components/property-filter'
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter'
import Table from '@cloudscape-design/components/table'
import RadioGroup from '@cloudscape-design/components/radio-group'
import Input from '@cloudscape-design/components/input'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Badge from '@cloudscape-design/components/badge'
type BadgeColor = 'blue' | 'green' | 'grey' | 'red' | 'severity-critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'severity-neutral'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Spinner from '@cloudscape-design/components/spinner'
import Modal from '@cloudscape-design/components/modal'
import FormField from '@cloudscape-design/components/form-field'
import Autosuggest from '@cloudscape-design/components/autosuggest'
import Select from '@cloudscape-design/components/select'
import type { SelectProps } from '@cloudscape-design/components/select'
import Multiselect from '@cloudscape-design/components/multiselect'
import Header from '@cloudscape-design/components/header'
import Alert from '@cloudscape-design/components/alert'
import Flashbar from '@cloudscape-design/components/flashbar'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import ButtonDropdown from '@cloudscape-design/components/button-dropdown'
import CollectionPreferences from '@cloudscape-design/components/collection-preferences'
import type { CollectionPreferencesProps } from '@cloudscape-design/components/collection-preferences'
import type { Provider, FitScore, ProviderStatus } from '@/lib/types'
import { useProviders, useAddProvidersToPipeline, useEnrichProviderCms, useRemoveProvider, useUpdateProviderStatus, usePrefetchProviderCms, useSearchProvidersByName, useSetProvidersCampaignSelection } from '@/hooks/useProviders'
import type { ProviderNameSearchResult } from '@/hooks/useProviders'
import { useDiscoverProviders } from '@/hooks/useDiscoverProviders'
import { useDiscoveryStore } from '@/state/discoveryStore'
import { useCityAutocomplete } from '@/hooks/useCityAutocomplete'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import GenerateEmailModal from './GenerateEmailModal'
import EditProviderModal from './EditProviderModal'
import { computeTemperature } from './ProviderDetailPanel'
import ProviderDirectoryCard from './ProviderDirectoryCard'
import styles from './DirectoryTab.module.css'


// Specialty groups for discovery — values match NPI Registry taxonomy_description strings
const SPECIALTY_GROUPS = [
  {
    group: 'Respiratory & Sleep',
    options: ['Pulmonary Disease', 'Sleep Medicine', 'Critical Care Medicine', 'Respiratory Therapy'],
  },
  {
    group: 'Primary & Internal Medicine',
    options: ['Internal Medicine', 'Family Medicine', 'Geriatric Medicine', 'General Practice'],
  },
  {
    group: 'Cardiology & Neurology',
    options: ['Cardiovascular Disease', 'Neurology', 'Vascular Neurology'],
  },
  {
    group: 'Oncology & Surgery',
    options: ['Medical Oncology', 'Radiation Oncology', 'Orthopedic Surgery', 'General Surgery'],
  },
  {
    group: 'Rehabilitation',
    options: ['Physical Therapist', 'Occupational Therapist', 'Speech-Language Pathologist'],
  },
  {
    group: 'Post-Acute & Long-Term Care',
    options: ['Skilled Nursing Facility', 'Assisted Living Facility', 'Nursing Care', 'Hospice', 'Home Health Agency'],
  },
  {
    group: 'Care Coordination',
    options: ['Case Management', 'Clinical Social Worker', 'Home Health Aide', 'Community Health Worker'],
  },
]

const SPECIALTY_OPTIONS = SPECIALTY_GROUPS.flatMap(g => g.options.map(opt => ({ label: opt, value: opt })))

const RADIUS_OPTIONS: SelectProps.Option[] = [
  { label: 'City only', value: '0' },
  { label: '10 miles', value: '10' },
  { label: '25 miles', value: '25' },
  { label: '50 miles', value: '50' },
]

const ENUMERATION_TYPE_OPTIONS: SelectProps.Option[] = [
  { label: 'Both', value: '' },
  { label: 'Individual', value: 'ind' },
  { label: 'Organization', value: 'org' },
]

const NAME_SEARCH_ENUMERATION_OPTIONS: SelectProps.Option[] = [
  { label: 'Both', value: 'both' },
  { label: 'Individual', value: 'NPI-1' },
  { label: 'Organization', value: 'NPI-2' },
]

const DISCOVERY_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'provider_name', propertyLabel: 'Name', groupValuesLabel: 'Name values', operators: [':', '!:', '=', '!='] },
  { key: 'specialty', propertyLabel: 'Specialty', groupValuesLabel: 'Specialty values', operators: [':', '!:', '=', '!='] },
  { key: 'city', propertyLabel: 'City', groupValuesLabel: 'City values', operators: [':', '!:', '=', '!='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['=', '!='] },
]

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: 'provider_name',
    propertyLabel: 'Name',
    groupValuesLabel: 'Name values',
    operators: [':', '!:', '=', '!='],
  },
  {
    key: 'specialty',
    propertyLabel: 'Specialty',
    groupValuesLabel: 'Specialty values',
    operators: [':', '!:', '=', '!='],
  },
  {
    key: 'city',
    propertyLabel: 'City',
    groupValuesLabel: 'City values',
    operators: [':', '!:', '=', '!='],
  },
  {
    key: 'state',
    propertyLabel: 'State',
    groupValuesLabel: 'State values',
    operators: ['=', '!='],
  },
  {
    key: 'status',
    propertyLabel: 'Status',
    groupValuesLabel: 'Status values',
    operators: ['=', '!='],
  },
  {
    key: 'npi',
    propertyLabel: 'NPI',
    groupValuesLabel: 'NPI values',
    operators: ['='],
  },
  {
    key: 'prescribing_volume_annual_usd',
    propertyLabel: 'CMS Volume',
    groupValuesLabel: 'Volume values',
    operators: ['=', '!=', '>', '<', '>=', '<='],
  },
  {
    key: 'cms_no_data',
    propertyLabel: 'CMS data missing',
    groupValuesLabel: 'CMS data missing values',
    operators: ['='],
  },
  {
    key: '_fit_label',
    propertyLabel: 'Fit Score',
    groupValuesLabel: 'Fit Score values',
    operators: ['=', '!='],
  },
]

const STORAGE_KEY = 'sabar:providers:preferences'

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 20,
  visibleContent: ['provider_name', 'specialty', 'location', 'status', 'cms_volume', 'fit_score', 'temperature', 'signals', 'row_actions'],
  wrapLines: false,
  stripedRows: false,
}

const CONTENT_DISPLAY_OPTIONS: CollectionPreferencesProps.VisibleContentOptionsGroup[] = [
  {
    label: 'Provider info',
    options: [
      { id: 'provider_name', label: 'Name', editable: false },
      { id: 'specialty', label: 'Specialty' },
      { id: 'location', label: 'Location' },
      { id: 'status', label: 'Status' },
    ],
  },
  {
    label: 'CMS data',
    options: [
      { id: 'cms_volume', label: 'CMS Volume' },
      { id: 'fit_score', label: 'Fit Score' },
    ],
  },
  {
    label: 'Engagement',
    options: [
      { id: 'temperature', label: 'Temperature' },
      { id: 'signals', label: 'Signals' },
    ],
  },
  {
    label: 'Actions',
    options: [
      { id: 'row_actions', label: 'Edit', editable: false },
    ],
  },
]

const NON_EDITABLE_COLUMNS = CONTENT_DISPLAY_OPTIONS
  .flatMap(g => g.options)
  .filter(o => o.editable === false)
  .map(o => o.id)

function loadPreferences(): CollectionPreferencesProps.Preferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      const visibleContent = parsed.visibleContent
        ? [...new Set([...NON_EDITABLE_COLUMNS, ...parsed.visibleContent])]
        : DEFAULT_PREFERENCES.visibleContent
      return { ...DEFAULT_PREFERENCES, ...parsed, visibleContent }
    }
  } catch {}
  return DEFAULT_PREFERENCES
}

interface DirectoryTabProps {
  clientId: string
  onProviderSelect: (provider: Provider | null) => void
}

function computeFitScore(p: Provider): FitScore {
  let score = 0
  const talking_points: string[] = []
  if (p.prescribing_volume_annual_usd !== null) {
    if (p.prescribing_volume_annual_usd > 100_000) { score += 4; talking_points.push(`$${(p.prescribing_volume_annual_usd/1000).toFixed(0)}k annual Medicare volume`) }
    else if (p.prescribing_volume_annual_usd > 50_000) { score += 2; talking_points.push(`$${(p.prescribing_volume_annual_usd/1000).toFixed(0)}k annual Medicare volume`) }
  }
  const signals = p.engagement_signals ?? []
  if (signals.length > 0) { score += 2; talking_points.push(`${signals.length} prior engagement signal(s)`) }
  if (p.cms_rank_by_volume !== null && p.cms_rank_by_volume <= 100) { score += 1; talking_points.push(`Ranked #${p.cms_rank_by_volume} by Medicare volume`) }
  const total = Math.min(score, 10)
  return { total, label: total >= 7 ? 'Strong Match' : total >= 4 ? 'Good Fit' : 'Low Priority', color: (total >= 7 ? 'green' : total >= 4 ? 'blue' : 'grey') as BadgeColor, talking_points }
}

export default function DirectoryTab({ clientId, onProviderSelect }: DirectoryTabProps) {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const { providers, loading } = useProviders(clientId)
  const { discover, isStreaming: discovering } = useDiscoverProviders()
  const discoveryStatus = useDiscoveryStore((s) => s.status)
  const discoverySteps = useDiscoveryStore((s) => s.steps)
  const discoveryErrorMessage = useDiscoveryStore((s) => s.errorMessage)
  const discoveredProviders = useDiscoveryStore((s) => s.providers)
  const { addProviders } = useAddProvidersToPipeline()
  const { enrichCms } = useEnrichProviderCms()
  const { prefetch } = usePrefetchProviderCms()
  const { removeProvider } = useRemoveProvider()
  const { updateStatus } = useUpdateProviderStatus()
  const { setProvidersCampaignSelection } = useSetProvidersCampaignSelection()
  const [selectedItems, setSelectedItems] = useState<Array<Provider & { _fit_label: string }>>([])
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null)

  useEffect(() => {
    setSelectedItems(prev => {
      if (prev.length === 0) return prev
      const updated = prev.map(sel => {
        const p = providers.find(q => q._id === sel._id)
        return p ? { ...p, _fit_label: computeFitScore(p).label } : sel
      })
      return updated.every((p, i) => p === prev[i]) ? prev : updated
    })
  }, [providers])

  const [discoveryOpen, setDiscoveryOpen] = useState(false)
  const [discoveryPhase, setDiscoveryPhase] = useState<'search' | 'results'>('search')
  const enrichedDiscoveryNpis = useRef<Set<string>>(new Set())
  const inflightCount = useRef(0)
  const [selectedDiscoveredItems, setSelectedDiscoveredItems] = useState<Provider[]>([])
  const [selectedSpecialties, setSelectedSpecialties] = useState<SelectProps.Option[]>([])
  const [maxRecords, setMaxRecords] = useState<SelectProps.Option>({ label: '20', value: '20' })
  const [radius, setRadius] = useState<SelectProps.Option>({ label: 'City only', value: '0' })
  const [enumerationType, setEnumerationType] = useState<SelectProps.Option>({ label: 'Both', value: '' })
  const [addingAll, setAddingAll] = useState(false)
  const [addingNpis, setAddingNpis] = useState<Set<string>>(new Set())
  const [addAllConfirmOpen, setAddAllConfirmOpen] = useState(false)
  const [discoveryError, setDiscoveryError] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>(loadPreferences)
  const [generateModalProvider, setGenerateModalProvider] = useState<Provider | null>(null)
  const [campaignFlash, setCampaignFlash] = useState<FlashbarProps.MessageDefinition[]>([])

  // Name search state (inside Discover modal)
  const [searchMode, setSearchMode] = useState<'specialty' | 'name'>('specialty')
  const [nameQuery, setNameQuery] = useState('')
  const [nameEnumerationType, setNameEnumerationType] = useState<SelectProps.Option>(NAME_SEARCH_ENUMERATION_OPTIONS[0])
  const [nameAddingNpis, setNameAddingNpis] = useState<Set<string>>(new Set())
  const [locallyAddedNpis, setLocallyAddedNpis] = useState<Set<string>>(new Set())
  const { searchByName, results: nameResults, loading: nameLoading } = useSearchProvidersByName()

  const { inputValue, options, city, state, loading: cityLoading, handleChange, handleSelect, reset: resetCity } = useCityAutocomplete()

  const [discoveryPrefs, setDiscoveryPrefs] = useState<{ pageSize: number }>({ pageSize: 20 })

  const isEnriching = discoveryPhase === 'results' && discoveredProviders.some(p => p.prescribing_volume_annual_usd === null && !p.cms_no_data)

  const {
    items: discoveryItems,
    collectionProps: discoveryCollectionProps,
    paginationProps: discoveryPaginationProps,
    propertyFilterProps: discoveryFilterProps,
    filteredItemsCount: discoveryFilteredCount,
  } = useCollection(discoveredProviders, {
    propertyFiltering: {
      filteringProperties: DISCOVERY_FILTERING_PROPERTIES,
      empty: <Box textAlign="center" color="text-body-secondary" padding="l">No providers found</Box>,
      noMatch: <Box textAlign="center" color="text-body-secondary" padding="l">No providers match the current filters</Box>,
    },
    sorting: {
      defaultState: {
        isDescending: true,
        sortingColumn: { sortingField: 'prescribing_volume_annual_usd' },
      },
    },
    pagination: { pageSize: discoveryPrefs.pageSize },
  })

  const providersWithFitLabel = useMemo(
    () => providers.map(p => ({ ...p, _fit_label: computeFitScore(p).label })),
    [providers],
  )

  const { items, collectionProps, paginationProps, propertyFilterProps } = useCollection(providersWithFitLabel, {
    propertyFiltering: {
      filteringProperties: FILTERING_PROPERTIES,
      empty: 'No providers found',
      noMatch: 'No providers match the current filters',
    },
    sorting: { defaultState: { isDescending: true, sortingColumn: { sortingField: 'prescribing_volume_annual_usd' } } },
    pagination: { pageSize: isMobile ? 10 : (preferences.pageSize ?? 20) },
  })

  function handlePreferencesChange(newPrefs: CollectionPreferencesProps.Preferences) {
    setPreferences(newPrefs)
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs)) } catch {}
  }

  const enrichedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!providers.length) return
    providers
      .filter(p => p.prescribing_volume_annual_usd === null && !p.cms_no_data && !enrichedRef.current.has(p._id))
      .forEach(p => {
        enrichedRef.current.add(p._id)
        enrichCms(clientId, p._id).catch(() => {})
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, clientId])

  // Prefetch CMS for all discovered providers, throttled to 10 concurrent
  useEffect(() => {
    if (discoveryPhase !== 'results') return
    const toEnrich = discoveredProviders.filter(
      p => p.prescribing_volume_annual_usd === null && !p.cms_no_data && !enrichedDiscoveryNpis.current.has(p.npi)
    )
    toEnrich.forEach(p => {
      if (inflightCount.current >= 10) return
      enrichedDiscoveryNpis.current.add(p.npi)
      inflightCount.current++
      prefetch(p.npi).then(result => {
        const cms = result.data?.prefetchProviderCms
        const current = useDiscoveryStore.getState().providers
        if (!cms) {
          useDiscoveryStore.getState().setProviders(current.map(d => d.npi === p.npi ? { ...d, cms_no_data: true } : d))
          return
        }
        useDiscoveryStore.getState().setProviders(current.map(d => d.npi === cms.npi ? { ...d, ...cms } : d))
      }).catch(() => {
        const current = useDiscoveryStore.getState().providers
        useDiscoveryStore.getState().setProviders(current.map(d => d.npi === p.npi ? { ...d, cms_no_data: true } : d))
      }).finally(() => { inflightCount.current-- })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveredProviders, discoveryPhase])

  const handleDiscoverClick = () => {
    setDiscoveryOpen(true)
    setDiscoveryError('')
  }

  const handleDiscoverSearch = async () => {
    setDiscoveryError('')
    if (!selectedSpecialties.length || !city || !state) {
      setDiscoveryError('Select at least one specialty and select a city from the suggestions')
      return
    }

    const specialtyList = selectedSpecialties.map(s => s.value).filter((v): v is string => v != null)
    const radiusValue = radius.value !== '0' ? Number(radius.value) : undefined
    const enumTypeValue = enumerationType.value || undefined
    enrichedDiscoveryNpis.current = new Set()
    inflightCount.current = 0
    setDiscoveryPhase('results')
    await discover(clientId, {
      specialties: specialtyList,
      city,
      state,
      radius: radiusValue,
      enumerationType: enumTypeValue,
    })
  }

  const handleAddProviders = async () => {
    await addProviders(clientId, selectedDiscoveredItems)
    const addedNpis = new Set(selectedDiscoveredItems.map(p => p.npi))
    useDiscoveryStore.getState().setProviders(discoveredProviders.filter(p => !addedNpis.has(p.npi)))
    setSelectedDiscoveredItems([])
  }

  const handleRowAdd = async (provider: Provider) => {
    setAddingNpis(prev => { const next = new Set(prev); next.add(provider.npi); return next })
    try {
      await addProviders(clientId, [provider])
      useDiscoveryStore.getState().setProviders(discoveredProviders.filter(p => p.npi !== provider.npi))
      setSelectedDiscoveredItems(prev => prev.filter(p => p.npi !== provider.npi))
    } catch {
      // leave row visible on failure
    } finally {
      setAddingNpis(prev => { const next = new Set(prev); next.delete(provider.npi); return next })
    }
  }

  const doAddAll = async (visible: Provider[]) => {
    setAddingAll(true)
    try {
      await addProviders(clientId, visible)
      const visibleNpis = new Set(visible.map(p => p.npi))
      useDiscoveryStore.getState().setProviders(discoveredProviders.filter(p => !visibleNpis.has(p.npi)))
      setSelectedDiscoveredItems([])
    } catch {
      // leave rows visible on failure
    } finally {
      setAddingAll(false)
      setAddAllConfirmOpen(false)
    }
  }

  const handleAddAll = () => {
    if (discoveredProviders.length > 10) {
      setAddAllConfirmOpen(true)
    } else {
      void doAddAll(discoveredProviders)
    }
  }

  const handleDeleteConfirmed = async () => {
    setDeleting(true)
    try {
      await Promise.all(selectedItems.map(p => removeProvider(clientId, p._id)))
      setSelectedItems([])
    } finally {
      setDeleting(false)
      setDeleteConfirmOpen(false)
    }
  }

  const handleCardMarkAs = (provider: Provider, status: string) => {
    void updateStatus(clientId, provider._id, status as ProviderStatus, {
      date: new Date().toISOString(),
      signal_type: 'manual_contact',
      detail: `Marked as ${status}`,
    })
  }

  const handleNameSearch = () => {
    if (!nameQuery.trim()) return
    const enumType = nameEnumerationType.value === 'both' ? undefined : nameEnumerationType.value
    void searchByName(clientId, nameQuery.trim(), enumType, 20)
    setLocallyAddedNpis(new Set())
    setDiscoveryPhase('results')
  }

  const handleNameRowAdd = async (item: ProviderNameSearchResult) => {
    setNameAddingNpis(prev => { const next = new Set(prev); next.add(item.npi); return next })
    try {
      await addProviders(clientId, [{
        _id: item.npi,
        provider_name: item.provider_name,
        specialty: item.specialty,
        practice_name: item.practice_name,
        city: item.city,
        state: item.state,
        npi: item.npi,
        prescribing_volume_annual_usd: null,
        dme_product_mix: [],
      } as Provider])
      setLocallyAddedNpis(prev => { const next = new Set(prev); next.add(item.npi); return next })
    } catch {
      // leave row visible on failure
    } finally {
      setNameAddingNpis(prev => { const next = new Set(prev); next.delete(item.npi); return next })
    }
  }

  const PROPERTY_FILTER_STRINGS = {
    filteringAriaLabel: 'Filter providers',
    filteringPlaceholder: 'Filter by name, specialty, status…',
    groupPropertiesText: 'Properties',
    groupValuesText: 'Values',
    operatorsText: 'Operators',
    operationAndText: 'and',
    operationOrText: 'or',
    operatorLessText: 'Less than',
    operatorLessOrEqualText: 'Less than or equal',
    operatorGreaterText: 'Greater than',
    operatorGreaterOrEqualText: 'Greater than or equal',
    operatorContainsText: 'Contains',
    operatorDoesNotContainText: 'Does not contain',
    operatorEqualsText: 'Equals',
    operatorDoesNotEqualText: 'Does not equal',
    editTokenHeader: 'Edit filter',
    propertyText: 'Property',
    operatorText: 'Operator',
    valueText: 'Value',
    cancelActionText: 'Cancel',
    applyActionText: 'Apply',
    allPropertiesLabel: 'All properties',
    tokenLimitShowMore: 'Show more',
    tokenLimitShowFewer: 'Show fewer',
    clearFiltersText: 'Clear filters',
    removeTokenButtonAriaLabel: (token: { propertyKey?: string; operator: string; value: string }) =>
      `Remove filter: ${token.propertyKey ?? ''} ${token.operator} ${token.value}`,
    enteredTextLabel: (text: string) => `Use: "${text}"`,
  }

  const DISCOVERY_I18N = {
    filteringAriaLabel: 'Filter providers',
    filteringPlaceholder: 'Filter by name, specialty, city…',
    groupPropertiesText: 'Properties',
    groupValuesText: 'Values',
    operatorsText: 'Operators',
    operationAndText: 'and',
    operationOrText: 'or',
    operatorContainsText: 'Contains',
    operatorDoesNotContainText: 'Does not contain',
    operatorEqualsText: 'Equals',
    operatorDoesNotEqualText: 'Does not equal',
    editTokenHeader: 'Edit filter',
    propertyText: 'Property',
    operatorText: 'Operator',
    valueText: 'Value',
    cancelActionText: 'Cancel',
    applyActionText: 'Apply',
    allPropertiesLabel: 'All properties',
    tokenLimitShowMore: 'Show more',
    tokenLimitShowFewer: 'Show fewer',
    clearFiltersText: 'Clear filters',
    removeTokenButtonAriaLabel: (token: { propertyKey?: string; operator: string; value: string }) =>
      `Remove filter: ${token.propertyKey ?? ''} ${token.operator} ${token.value}`,
    enteredTextLabel: (text: string) => `Use: "${text}"`,
  }

  const discoverySearchForm = (
    <SpaceBetween size="m">
      {discoveryError && (
        <Alert type="error" dismissible onDismiss={() => setDiscoveryError('')}>{discoveryError}</Alert>
      )}
      <FormField label="Search method">
        <RadioGroup
          value={searchMode}
          onChange={({ detail }) => setSearchMode(detail.value as 'specialty' | 'name')}
          items={[
            { value: 'specialty', label: 'By specialty & location' },
            { value: 'name', label: 'By provider name' },
          ]}
        />
      </FormField>
      {searchMode === 'specialty' ? (
        <SpaceBetween size="m">
          <FormField label="Specialties" description={`${selectedSpecialties.length} selected`}>
            <Multiselect
              selectedOptions={selectedSpecialties}
              onChange={(e) => setSelectedSpecialties(e.detail.selectedOptions as typeof SPECIALTY_OPTIONS)}
              options={SPECIALTY_OPTIONS}
              placeholder="Select specialties…"
            />
          </FormField>
          <FormField
            label="City"
            errorText={inputValue && !state ? 'Select a city from the dropdown suggestions' : undefined}
          >
            <Autosuggest
              value={inputValue}
              onChange={({ detail }) => handleChange(detail.value)}
              onSelect={({ detail }) => handleSelect(detail.value)}
              options={options}
              statusType={cityLoading ? 'loading' : 'finished'}
              loadingText="Searching cities..."
              placeholder="Start typing a city..."
              empty="No cities found"
              invalid={!!inputValue && !state}
            />
          </FormField>
          {state && (
            <FormField label="State">
              <Box variant="p">{state}</Box>
            </FormField>
          )}
          <FormField label="Radius">
            <Select
              selectedOption={radius}
              onChange={e => setRadius(e.detail.selectedOption as SelectProps.Option)}
              options={RADIUS_OPTIONS}
            />
          </FormField>
          <FormField label="Provider Type">
            <Select
              selectedOption={enumerationType}
              onChange={e => setEnumerationType(e.detail.selectedOption as SelectProps.Option)}
              options={ENUMERATION_TYPE_OPTIONS}
            />
          </FormField>
          <FormField label="Max Results to Show">
            <Select
              selectedOption={maxRecords}
              onChange={(e) => setMaxRecords(e.detail.selectedOption as SelectProps.Option)}
              options={[
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ]}
            />
          </FormField>
        </SpaceBetween>
      ) : (
        <SpaceBetween size="m">
          <FormField label="Provider name">
            <Input
              value={nameQuery}
              onChange={({ detail }) => setNameQuery(detail.value)}
              placeholder="Enter full or partial name…"
              onKeyDown={({ detail }) => { if (detail.key === 'Enter') handleNameSearch() }}
            />
          </FormField>
          <FormField label="Provider type">
            <Select
              selectedOption={nameEnumerationType}
              onChange={e => setNameEnumerationType(e.detail.selectedOption as SelectProps.Option)}
              options={NAME_SEARCH_ENUMERATION_OPTIONS}
            />
          </FormField>
        </SpaceBetween>
      )}
    </SpaceBetween>
  )

  const discoveryResultsColumnDefs = [
    { id: 'provider_name', header: 'Name', cell: (item: Provider) => item.provider_name, sortingField: 'provider_name' },
    { id: 'specialty', header: 'Specialty', cell: (item: Provider) => item.specialty, sortingField: 'specialty' },
    { id: 'location', header: 'Location', cell: (item: Provider) => `${item.city}, ${item.state}`, sortingField: 'city' },
    { id: 'practice_name', header: 'Practice', cell: (item: Provider) => item.practice_name, sortingField: 'practice_name' },
    {
      id: 'cms_volume',
      header: 'CMS Volume',
      cell: (item: Provider) => {
        if (item.cms_no_data) return <Box color="text-body-secondary">—</Box>
        if (item.prescribing_volume_annual_usd === null) return <StatusIndicator type="loading">Loading</StatusIndicator>
        const dme = `$${(item.prescribing_volume_annual_usd / 1000).toFixed(0)}k`
        const total = item.medicare_volume?.total_usd
        return total ? (
          <SpaceBetween size="xxxs">
            <Box>{dme} DME</Box>
            <Box variant="small" color="text-body-secondary">${(total/1000).toFixed(0)}k total</Box>
          </SpaceBetween>
        ) : dme
      },
      sortingField: 'prescribing_volume_annual_usd',
    },
    {
      id: 'fit_score',
      header: 'Fit',
      cell: (item: Provider) => {
        if (item.prescribing_volume_annual_usd === null && !item.cms_no_data) return <StatusIndicator type="loading">Loading</StatusIndicator>
        const score = computeFitScore(item)
        return <Badge color={score.color as 'green' | 'blue' | 'grey'}>{score.label}</Badge>
      },
    },
    {
      id: 'actions',
      header: '',
      cell: (item: Provider) => {
        const isAdding = addingNpis.has(item.npi)
        return (
          <Button
            loading={isAdding}
            disabled={addingAll || isAdding}
            onClick={() => void handleRowAdd(item)}
          >
            Add
          </Button>
        )
      },
    },
  ]

  const discoveryResultsHeader = (
    <Header
      counter={
        selectedDiscoveredItems.length > 0
          ? `(${selectedDiscoveredItems.length}/${discoveredProviders.length})`
          : discoveryFilteredCount !== undefined && discoveryFilteredCount !== discoveredProviders.length
            ? `(${discoveryFilteredCount}/${discoveredProviders.length})`
            : `(${discoveredProviders.length})`
      }
      description={isEnriching ? <StatusIndicator type="loading">Enriching provider data…</StatusIndicator> : undefined}
      actions={
        <Button
          variant="primary"
          loading={addingAll}
          disabled={discoveredProviders.length === 0}
          onClick={handleAddAll}
        >
          Add All to Pipeline
        </Button>
      }
    >
      Discovered Providers
    </Header>
  )

  const discoveryResultsPreferences = (
    <CollectionPreferences
      title="Preferences"
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      preferences={{ pageSize: discoveryPrefs.pageSize }}
      onConfirm={({ detail }) => setDiscoveryPrefs({ pageSize: detail.pageSize ?? 20 })}
      pageSizePreference={{
        title: 'Rows per page',
        options: [
          { value: 10, label: '10 rows' },
          { value: 20, label: '20 rows' },
          { value: 50, label: '50 rows' },
        ],
      }}
    />
  )

  const discoveryModalFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="s">
        {discoveryPhase === 'search' ? (
          <>
            <Button onClick={() => setDiscoveryOpen(false)}>Cancel</Button>
            {searchMode === 'specialty' ? (
              <Button
                onClick={handleDiscoverSearch}
                variant="primary"
                loading={discovering}
                disabled={discovering || !selectedSpecialties.length || !city || !state}
              >
                Search
              </Button>
            ) : (
              <Button
                onClick={handleNameSearch}
                variant="primary"
                disabled={!nameQuery.trim()}
              >
                Search
              </Button>
            )}
          </>
        ) : searchMode === 'specialty' ? (
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={() => setDiscoveryPhase('search')} disabled={discovering}>Back</Button>
            <Button
              onClick={handleAddProviders}
              variant="primary"
              disabled={selectedDiscoveredItems.length === 0 || addingAll}
            >
              Add {selectedDiscoveredItems.length} to Pipeline
            </Button>
          </SpaceBetween>
        ) : (
          <Button onClick={() => { setDiscoveryPhase('search'); setLocallyAddedNpis(new Set()) }}>Back</Button>
        )}
      </SpaceBetween>
    </Box>
  )

  const stepLabel: Record<string, string> = {
    load_pipeline: 'Loading pipeline',
    npi_search: 'Searching NPI registry',
    build_results: 'Building results',
  }

  const discoveryStepIndicator = (
    <Box padding="l">
      <SpaceBetween size="s">
        {(['load_pipeline', 'npi_search', 'build_results'] as const).map((step) => {
          const s = discoverySteps[step]
          const indicatorType =
            s === 'in_progress' ? 'loading' :
            s === 'done' ? 'success' :
            s === 'error' ? 'error' : 'pending'
          return (
            <StatusIndicator key={step} type={indicatorType}>
              {stepLabel[step]}
            </StatusIndicator>
          )
        })}
      </SpaceBetween>
    </Box>
  )

  const nameResultsColumnDefs = [
    { id: 'provider_name', header: 'Provider Name', cell: (item: ProviderNameSearchResult) => item.provider_name, sortingField: 'provider_name' },
    { id: 'location', header: 'City/State', cell: (item: ProviderNameSearchResult) => `${item.city}, ${item.state}`, sortingField: 'city' },
    {
      id: 'already_in_pipeline',
      header: 'Already in Pipeline',
      cell: (item: ProviderNameSearchResult) => {
        const added = locallyAddedNpis.has(item.npi) || item.already_in_pipeline
        return <Badge color={added ? 'green' : 'grey'}>{added ? 'Yes' : 'No'}</Badge>
      },
    },
    {
      id: 'actions',
      header: '',
      minWidth: 100,
      cell: (item: ProviderNameSearchResult) => {
        const isAdding = nameAddingNpis.has(item.npi)
        const alreadyAdded = locallyAddedNpis.has(item.npi) || item.already_in_pipeline
        return (
          <Button
            loading={isAdding}
            disabled={alreadyAdded || isAdding}
            onClick={() => { void handleNameRowAdd(item) }}
          >
            Add
          </Button>
        )
      },
    },
  ]

  const discoveryModalContent = (
    discoveryPhase === 'search' ? (
      discovering ? (
        discoveryStepIndicator
      ) : discoverySearchForm
    ) : searchMode === 'name' ? (
      nameLoading ? (
        <Box textAlign="center" padding="xl">
          <Spinner size="large" />
        </Box>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <Table
            columnDefinitions={nameResultsColumnDefs}
            items={nameResults}
            empty={
              <Box textAlign="center" color="text-body-secondary" padding="l">
                No providers found. Try a different name.
              </Box>
            }
            header={<Header counter={nameResults.length > 0 ? `(${nameResults.length})` : undefined}>Results</Header>}
          />
        </div>
      )
    ) : (
      <SpaceBetween size="m">
        {discoveryStatus === 'running' && discoveryStepIndicator}
        {discoveryStatus === 'error' && discoveryErrorMessage && (
          <Alert type="error">{discoveryErrorMessage}</Alert>
        )}
        <div style={{overflow: 'auto'}}>
          <Table
            {...discoveryCollectionProps}
            stickyHeader
            columnDefinitions={discoveryResultsColumnDefs}
            items={discoveryItems}
            selectedItems={selectedDiscoveredItems}
            onSelectionChange={(e) => setSelectedDiscoveredItems(e.detail.selectedItems)}
            selectionType="multi"
            resizableColumns
            pagination={<Pagination {...discoveryPaginationProps} />}
            filter={
              <PropertyFilter
                {...discoveryFilterProps}
                filteringProperties={DISCOVERY_FILTERING_PROPERTIES}
                i18nStrings={DISCOVERY_I18N}
              />
            }
            preferences={discoveryResultsPreferences}
            header={discoveryResultsHeader}
          />
        </div>
      </SpaceBetween>
    )
  )

  if (isMobile) {
    return (
      <>
        <Cards
          {...collectionProps}
          items={items}
          loading={loading}
          loadingText="Loading providers…"
          cardsPerRow={[{ cards: 1 }, { minWidth: 640, cards: 2 }]}
          cardDefinition={{
            header: (item) => (
              <Link onFollow={() => onProviderSelect(item)}>{item.provider_name}</Link>
            ),
            sections: [
              {
                id: 'details',
                content: (item) => (
                  <ProviderDirectoryCard
                    provider={item}
                    onGenerateEmail={(p) => setGenerateModalProvider(p)}
                    onMarkAs={handleCardMarkAs}
                    onRemove={(p) => { setSelectedItems([{ ...p, _fit_label: computeFitScore(p).label }]); setDeleteConfirmOpen(true) }}
                  />
                ),
              },
            ],
          }}
          empty={
            <Box textAlign="center" color="text-body-secondary" padding="xxl">
              <Box variant="strong">No providers yet</Box>
              <Box variant="p">Click "Discover Providers" to find providers in your area.</Box>
            </Box>
          }
          header={
            <div className={styles.mobileCardsHeader}>
              <Header counter={`(${providers.length})`}>Providers</Header>
              <Button
                onClick={handleDiscoverClick}
                variant="primary"
                iconName="add-plus"
                ariaLabel="Discover Providers"
              />
            </div>
          }
          filter={
            <PropertyFilter
              {...propertyFilterProps}
              filteringProperties={FILTERING_PROPERTIES}
              i18nStrings={PROPERTY_FILTER_STRINGS}
            />
          }
          pagination={<Pagination {...paginationProps} />}
        />

        <Modal
          onDismiss={() => {
            setDiscoveryOpen(false)
            setDiscoveryPhase('search')
            setDiscoveryError('')
            setLocallyAddedNpis(new Set())
            resetCity()
          }}
          visible={discoveryOpen}
          header={discoveryPhase === 'search' ? 'Discover Providers' : 'Results'}
          size="large"
          height={500}
          footer={discoveryModalFooter}
        >
          {discoveryModalContent}
        </Modal>

        <Modal
          visible={addAllConfirmOpen}
          onDismiss={() => setAddAllConfirmOpen(false)}
          header="Add all to pipeline"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setAddAllConfirmOpen(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  loading={addingAll}
                  onClick={() => void doAddAll(discoveredProviders)}
                >
                  Add all {discoveredProviders.length} to Pipeline
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          Add all {discoveredProviders.length} providers to the pipeline?
        </Modal>

        <Modal
          visible={deleteConfirmOpen}
          onDismiss={() => setDeleteConfirmOpen(false)}
          header="Remove providers"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button variant="primary" loading={deleting} onClick={handleDeleteConfirmed}>Remove</Button>
              </SpaceBetween>
            </Box>
          }
        >
          Remove {selectedItems.length} provider{selectedItems.length !== 1 ? 's' : ''} from your pipeline? This cannot be undone.
        </Modal>
        {generateModalProvider && (
          <GenerateEmailModal
            provider={generateModalProvider}
            clientId={clientId}
            onDismiss={() => setGenerateModalProvider(null)}
          />
        )}
      </>
    )
  }

  return (
    <>
      {campaignFlash.length > 0 && <Flashbar items={campaignFlash} />}
      <Table
        {...collectionProps}
        columnDefinitions={[
          {
            id: 'provider_name',
            header: 'Name',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Link onFollow={() => onProviderSelect(item)}>{item.provider_name}</Link>
                {item.selectedForCampaign && <Badge color="blue">Campaign</Badge>}
              </SpaceBetween>
            ),
            sortingField: 'provider_name',
            width: '16%',
          },
          {
            id: 'specialty',
            header: 'Specialty',
            cell: (item) => item.specialty,
            sortingField: 'specialty',
            width: '13%',
          },
          {
            id: 'location',
            header: 'Location',
            cell: (item) => `${item.city}, ${item.state}`,
            sortingField: 'city',
            width: '10%',
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatusIndicator type={item.status === 'contacted' ? 'success' : 'info'}>{item.status}</StatusIndicator>,
            sortingField: 'status',
            width: '10%',
          },
          {
            id: 'cms_volume',
            header: 'CMS Volume',
            cell: (item) => {
              if (!item.prescribing_volume_annual_usd) return '—'
              const dme = `$${(item.prescribing_volume_annual_usd/1000).toFixed(0)}k`
              const total = item.medicare_volume?.total_usd
              return total ? (
                <SpaceBetween size="xxxs">
                  <Box>{dme} DME</Box>
                  <Box variant="small" color="text-body-secondary">${(total/1000).toFixed(0)}k total</Box>
                </SpaceBetween>
              ) : dme
            },
            sortingField: 'prescribing_volume_annual_usd',
            width: '9%',
          },
          {
            id: 'fit_score',
            header: 'Fit',
            cell: (item) => {
              const score = computeFitScore(item)
              const colorMap: Record<string, 'green' | 'blue' | 'grey'> = {
                emerald: 'green',
                amber: 'blue',
                zinc: 'grey',
              }
              return <Badge color={colorMap[score.color] ?? 'grey'}>{score.label}</Badge>
            },
            sortingComparator: (a, b) => computeFitScore(a).total - computeFitScore(b).total,
            width: '10%',
          },
          {
            id: 'temperature',
            header: 'Temp',
            cell: (item) => {
              const temp = computeTemperature(item.engagement_signals)
              const colorMap = { hot: 'red', warm: 'severity-medium', cold: 'grey' } as const
              return <Badge color={colorMap[temp]}>{temp}</Badge>
            },
            sortingComparator: (a, b) => {
              const rank = { hot: 3, warm: 2, cold: 1 } as const
              return rank[computeTemperature(a.engagement_signals)] - rank[computeTemperature(b.engagement_signals)]
            },
            width: '7%',
          },
          {
            id: 'signals',
            header: 'Signals',
            cell: (item) => {
              if (item.engagement_signals.length === 0) return <Box color="text-body-secondary">—</Box>
              return (
                <ExpandableSection headerText={String(item.engagement_signals.length)} variant="footer">
                  <SpaceBetween size="xs">
                    {item.engagement_signals.map((sig, i) => (
                      <Box key={i} variant="small">
                        {new Date(sig.date).toLocaleDateString()} — {sig.signal_type}
                        {sig.detail ? `: ${sig.detail}` : ''}
                      </Box>
                    ))}
                  </SpaceBetween>
                </ExpandableSection>
              )
            },
            sortingComparator: (a, b) => a.engagement_signals.length - b.engagement_signals.length,
            width: '10%',
          },
          {
            id: 'row_actions',
            header: '',
            cell: (item) => (
              <Button
                variant="inline-link"
                onClick={() => setEditingProvider(item)}
              >
                Edit
              </Button>
            ),
            width: '6%',
          },
        ]}
        items={items}
        selectedItems={selectedItems}
        onSelectionChange={(e) => { setSelectedItems(e.detail.selectedItems) }}
        selectionType="multi"
        loading={loading}
        resizableColumns
        wrapLines={preferences.wrapLines}
        stripedRows={preferences.stripedRows}
        visibleColumns={preferences.visibleContent}
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            onConfirm={({ detail }) => handlePreferencesChange(detail)}
            pageSizePreference={{
              title: 'Rows per page',
              options: [
                { value: 10, label: '10 rows' },
                { value: 20, label: '20 rows' },
                { value: 50, label: '50 rows' },
              ],
            }}
            visibleContentPreference={{
              title: 'Visible columns',
              options: CONTENT_DISPLAY_OPTIONS,
            }}
            wrapLinesPreference={{ label: 'Wrap lines', description: 'Wrap long cell content' }}
            stripedRowsPreference={{ label: 'Striped rows', description: 'Alternate row shading' }}
          />
        }
        empty={
          <Box textAlign="center" color="text-body-secondary" padding="xxl">
            <Box variant="strong">No providers yet</Box>
            <Box variant="p">Click "Discover Providers" to find providers in your area.</Box>
          </Box>
        }
        header={
          <Header
            counter={`(${providers.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={selectedItems.length === 0} onClick={() => setDeleteConfirmOpen(true)}>
                  Remove ({selectedItems.length})
                </Button>
                <ButtonDropdown
                  disabled={selectedItems.length === 0}
                  items={[
                    { id: 'contacted', text: 'Contacted' },
                    { id: 'engaged', text: 'Engaged' },
                    { id: 'referred', text: 'Referred' },
                    { id: 'inactive', text: 'Inactive' },
                  ]}
                  onItemClick={({ detail }) => {
                    const status = detail.id as ProviderStatus
                    Promise.all(selectedItems.map(p => updateStatus(clientId, p._id, status, {
                      date: new Date().toISOString(),
                      signal_type: 'manual_contact',
                      detail: `Marked as ${status}`,
                    })))
                  }}
                >
                  Mark as
                </ButtonDropdown>
                {/* <Button
                  disabled={selectedItems.length !== 1}
                  onClick={() => setGenerateModalProvider(selectedItems[0])}
                >
                  Generate Email
                </Button> */}
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={async () => {
                    const ids = selectedItems.map(p => p._id)
                    try {
                      await setProvidersCampaignSelection(clientId, ids, true)
                      setCampaignFlash([{
                        type: 'success',
                        content: `${ids.length} provider${ids.length !== 1 ? 's' : ''} added to campaign`,
                        dismissible: true,
                        onDismiss: () => setCampaignFlash([]),
                      }])
                    } catch {
                      setCampaignFlash([{
                        type: 'error',
                        content: 'Failed to add providers to campaign. Please try again.',
                        dismissible: true,
                        onDismiss: () => setCampaignFlash([]),
                      }])
                    }
                  }}
                >
                  Add to Campaign
                </Button>
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={async () => {
                    const ids = selectedItems.map(p => p._id)
                    try {
                      await setProvidersCampaignSelection(clientId, ids, false)
                      setCampaignFlash([{
                        type: 'success',
                        content: `${ids.length} provider${ids.length !== 1 ? 's' : ''} removed from campaign`,
                        dismissible: true,
                        onDismiss: () => setCampaignFlash([]),
                      }])
                    } catch {
                      setCampaignFlash([{
                        type: 'error',
                        content: 'Failed to remove providers from campaign. Please try again.',
                        dismissible: true,
                        onDismiss: () => setCampaignFlash([]),
                      }])
                    }
                  }}
                >
                  Remove from Campaign
                </Button>
                <Button onClick={handleDiscoverClick} variant="primary">Discover Providers</Button>
              </SpaceBetween>
            }
          >
            Providers
          </Header>
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            filteringProperties={FILTERING_PROPERTIES}
            i18nStrings={PROPERTY_FILTER_STRINGS}
          />
        }
      />

      <Modal
        onDismiss={() => {
          setDiscoveryOpen(false)
          setDiscoveryPhase('search')
          setDiscoveryError('')
          setLocallyAddedNpis(new Set())
          resetCity()
        }}
        visible={discoveryOpen}
        header={discoveryPhase === 'search' ? 'Discover Providers' : 'Results'}
        size="large"
        height={500}
        footer={discoveryModalFooter}
      >
        {discoveryModalContent}
      </Modal>

      <Modal
        visible={addAllConfirmOpen}
        onDismiss={() => setAddAllConfirmOpen(false)}
        header="Add all to pipeline"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAddAllConfirmOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                loading={addingAll}
                onClick={() => void doAddAll(discoveredProviders)}
              >
                Add all {discoveredProviders.length} to Pipeline
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Add all {discoveredProviders.length} providers to the pipeline?
      </Modal>

      <Modal
        visible={deleteConfirmOpen}
        onDismiss={() => setDeleteConfirmOpen(false)}
        header="Remove providers"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="primary" loading={deleting} onClick={handleDeleteConfirmed}>Remove</Button>
            </SpaceBetween>
          </Box>
        }
      >
        Remove {selectedItems.length} provider{selectedItems.length !== 1 ? 's' : ''} from your pipeline? This cannot be undone.
      </Modal>
      {generateModalProvider && (
        <GenerateEmailModal
          provider={generateModalProvider}
          clientId={clientId}
          onDismiss={() => setGenerateModalProvider(null)}
        />
      )}
      <EditProviderModal
        provider={editingProvider}
        clientId={clientId}
        onClose={() => setEditingProvider(null)}
      />
    </>
  )
}
